import React, { useEffect, useRef, useCallback } from "react";
import { formatDate } from "./PropertiesComponent";

// Debounce function
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const PropertyListView = ({
  filterSearchResult,
  isLoading,
  propertiesData,
  onLoadMore,
}: {
  filterSearchResult: any;
  isLoading: boolean;
  propertiesData: any[];
  onLoadMore: () => void;
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  const debouncedLoadMore = useCallback(
    debounce(() => {
      if (!isLoading) {
        onLoadMore();
      }
    }, 300),
    [isLoading, onLoadMore]
  );

  const handleScroll = useCallback(() => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollHeight - scrollTop <= clientHeight * 1.5) {
        debouncedLoadMore();
      }
    }
  }, [debouncedLoadMore]);

  useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (listElement) {
        listElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  const renderSkeletonRows = () => {
    return Array(2)
      .fill(null)
      .map((_, index) => (
        <tr key={`skeleton-${index}`}>
          <td className="py-4 px-3 border-r last:border-r-0">
            <div className="h-4 w-full rounded"></div>
          </td>
          <td className="py-4 px-3 border-r last:border-r-0">
            <div className="h-4 w-full rounded"></div>
          </td>
          <td className="py-4 px-3 border-r last:border-r-0">
            <div className="h-4 w-full rounded"></div>
          </td>
          <td className="py-4 px-3 border-r last:border-r-0">
            <div className="h-4 w-full rounded"></div>
          </td>
          <td className="py-4 px-3 border-r last:border-r-0">
            <div className="h-4 w-full rounded"></div>
          </td>
          <td className="py-4 px-3 border-r last:border-r-0">
            <div className="h-4 w-full rounded"></div>
          </td>
          <td className="py-4 px-3 border-r last:border-r-0">
            <div className="h-4 w-full rounded"></div>
          </td>
        </tr>
      ));
  };

  const tableHeaderClass =
    "px-3 py-4 text-left text-xs font-semibold text-gray-900 border-r last:border-r-0";
  const tableCellClass =
    "px-3 py-4 text-sm text-gray-500 border-r last:border-r-0";
  const scrollableContentClass =
    "block overflow-x-auto whitespace-nowrap scrollbar-hide capitalize";

  console.log("properties length ::: ", propertiesData.length);

  return (
    <div ref={listRef} className="overflow-y-auto max-h-[calc(100vh-10px)]">
      <table className="w-full table-fixed divide-y divide-gray-300">
        <colgroup>
          <col className="w-2/12" /> {/* Listing Id */}
          <col className="w-2/12" /> {/* Bank Name */}
          <col className="w-2/12" /> {/* Title */}
          <col className="w-2/12" /> {/* City */}
          <col className="w-2/12" /> {/* State */}
          <col className="w-2/12" /> {/* Reserve Price */}
          <col className="w-2/12" /> {/* Auction Date */}
        </colgroup>
        <thead className="sticky top-0 bg-gray-50">
          <tr>
            <th className={tableHeaderClass}>Listing Id</th>
            <th className={tableHeaderClass}>Bank Name</th>
            <th className={tableHeaderClass}>Title</th>
            <th className={tableHeaderClass}>City</th>
            <th className={tableHeaderClass}>State</th>
            <th className={tableHeaderClass}>Reserve Price</th>
            <th className={tableHeaderClass}>Auction Date</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {propertiesData.map((property: any) => (
            <tr key={property.listing_id}>
              <td className={tableCellClass}>
                <div className={scrollableContentClass}>
                  <a
                    href={`/auction-properties/${property?.listing_id}`}
                    className="inline-block px-2 py-1 bg-blue-500 text-white text-sm font-medium rounded hover:bg-blue-600"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {property?.listing_id}
                  </a>
                </div>
              </td>
              <td className={tableCellClass}>
                <div className={scrollableContentClass}>
                  {property?.dump?.bank?.bank_name}
                </div>
              </td>
              <td className={tableCellClass}>
                <div className={scrollableContentClass}>
                  {property?.dump?.asset?.sub_asset_type_name}
                </div>
              </td>
              <td className={tableCellClass}>
                <div className={scrollableContentClass}>
                  {property?.dump?.location?.city_name}
                </div>
              </td>
              <td className={tableCellClass}>
                <div className={scrollableContentClass}>
                  {property?.dump?.location?.state_name}
                </div>
              </td>
              <td className={tableCellClass}>
                <div className={scrollableContentClass}>
                  {property?.property_date_type !== "private_treaty" &&
                  property?.property_date_type !== "Yes" &&
                  property?.property_date_type !== "dated"
                    ? "Contact Us"
                    : property?.reserve_price}
                </div>
              </td>
              <td className={tableCellClass}>
                <div className={scrollableContentClass}>
                  {property?.property_date_type === "Yes" ||
                  property?.property_date_type === "dated"
                    ? formatDate(property?.auction_start_date_time)
                    : property?.property_date_type === "private_treaty"
                    ? "Private Treaty"
                    : "Coming Soon"}
                </div>
              </td>
            </tr>
          ))}
          {isLoading && renderSkeletonRows()}
        </tbody>
      </table>
    </div>
  );
};

export default PropertyListView;
